import { IIntegrationsQuicbooksSyncActivityAction } from "@wingspanhq/integrations/dist/lib/interfaces/quickbooks-api/integrationsQuickbooksSyncActivity";
import flatten from "lodash/flatten";
import { WSInfiniteQueryConfig, WSQueryConfig } from "@ws-react-query";
import {
  DateRangeFilter,
  getDateRangeFilterRanges
} from "../../Invoices/utils";
import {
  getIntegrationsQuickbooks,
  getIntegrationsQuickbooksAccountAssets,
  getIntegrationsQuickbooksAccountExpenses,
  getIntegrationsQuickbooksAccountLiabilities,
  getIntegrationsQuickbooksCustomers,
  getIntegrationsQuickbooksItems,
  getIntegrationsQuickbooksSyncActivities,
  getIntegrationsQuickbooksVendors,
  IntegrationsQuickbooksSyncActivitiesFilter,
  IntegrationsQuickbooksSyncActivitiesWithSumamry
} from "../../services/integrations";
import { Await } from "../../utils";
import { getAllEntries, WSServiceError } from "../../utils/serviceHelper";
import { useWSInfiniteQuery, useWSQuery } from "../helpers";
import {
  QUERY_INTEGRATIONS_QUICKBOOKS,
  QUERY_INTEGRATIONS_QUICKBOOKS_ACCOUNT_ASSETS,
  QUERY_INTEGRATIONS_QUICKBOOKS_ACCOUNT_EXPENSES,
  QUERY_INTEGRATIONS_QUICKBOOKS_ACCOUNT_LIABILITIES,
  QUERY_INTEGRATIONS_QUICKBOOKS_CUSTOMERS,
  QUERY_INTEGRATIONS_QUICKBOOKS_ITEMS,
  QUERY_INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES,
  QUERY_INTEGRATIONS_QUICKBOOKS_VENDORS
} from "./keys";

export const useQueryIntegrationsQuickbooks = (
  queryConfig?: WSQueryConfig<
    Await<ReturnType<typeof getIntegrationsQuickbooks>>,
    WSServiceError
  >
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS,
    getIntegrationsQuickbooks,
    queryConfig
  );

export interface IntegrationsQuickbooksSyncActivitiesQueryFilters {
  onlyErrors: boolean;
  actions: IIntegrationsQuicbooksSyncActivityAction[];
  createdAtRange: DateRangeFilter;
  customCreatedAtRange?: Date[];
  entityId?: string;
  displayNameSearch?: string;
}

export const INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES_QUERY_FILTERS_DEFAULT: IntegrationsQuickbooksSyncActivitiesQueryFilters =
  {
    onlyErrors: false,
    actions: [
      IIntegrationsQuicbooksSyncActivityAction.Create,
      IIntegrationsQuicbooksSyncActivityAction.Delete,
      IIntegrationsQuicbooksSyncActivityAction.Update
    ],
    createdAtRange: DateRangeFilter.All
  };

export const useQueryIntegrationsQuickbooksSyncActivities = (
  filters?: IntegrationsQuickbooksSyncActivitiesQueryFilters,
  config?: { size?: number } & WSInfiniteQueryConfig<
    IntegrationsQuickbooksSyncActivitiesWithSumamry,
    WSServiceError
  >
) => {
  // const userId = useUserId();

  const _filters =
    filters || INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES_QUERY_FILTERS_DEFAULT;

  const queryKey = QUERY_INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES;
  const size = config?.size || 100;

  const query = useWSInfiniteQuery<
    IntegrationsQuickbooksSyncActivitiesWithSumamry,
    WSServiceError
  >(
    [queryKey, { filters, size }],
    ({ pageParam = 1 }) => {
      const filter: IntegrationsQuickbooksSyncActivitiesFilter = {
        // userId
      };

      const createdAtRange = getDateRangeFilterRanges(
        _filters.createdAtRange,
        _filters.customCreatedAtRange
      );

      if (createdAtRange) {
        filter["createdAt"] = {
          ">": createdAtRange[0],
          "<": createdAtRange[1]
        };
      }

      if (_filters.entityId) {
        filter["entityId"] = _filters.entityId;
      }

      const allowedActions: IIntegrationsQuicbooksSyncActivityAction[] = [];

      if (_filters.actions) {
        _filters.actions.forEach(action => allowedActions.push(action));
        filter.action = {
          in: allowedActions
        };
      }

      if (_filters.onlyErrors) {
        filter.isError = true;
      }

      if (_filters.displayNameSearch) {
        filter.displayName = {
          like: _filters.displayNameSearch
        };
      }

      return getIntegrationsQuickbooksSyncActivities({
        filter,
        page: {
          size,
          number: pageParam
        },
        sort: {
          updatedAt: "desc"
        }
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.length < size) {
          return undefined;
        } else {
          return allPages.length + 1;
        }
      },
      ...config
    }
  );

  return {
    ...query,
    data: query.data
      ? {
          data: flatten(query.data.pages?.map(page => page.data)),
          summary: query.data.pages?.[0]?.summary
        }
      : undefined
  };
};

export const useQueryIntegrationsQuickbooksSyncActivitiesPreview = (
  queryConfig?: WSQueryConfig<any, WSServiceError>
) => {
  return useQueryIntegrationsQuickbooksSyncActivities(
    INTEGRATIONS_QUICKBOOKS_SYNC_ACTIVITIES_QUERY_FILTERS_DEFAULT,
    {
      size: 2,
      ...queryConfig
    }
  );
};

export const useQueryIntegrationsQuickbooksAccountAssets = (
  queryConfig?: WSQueryConfig<any, WSServiceError>
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS_ACCOUNT_ASSETS,
    () => getAllEntries(getIntegrationsQuickbooksAccountAssets),
    { ...queryConfig, refetchOnMount: true }
  );

export const useQueryIntegrationsQuickbooksAccountExpenses = (
  queryConfig?: WSQueryConfig<
    Await<ReturnType<typeof getIntegrationsQuickbooksAccountExpenses>>,
    WSServiceError
  >
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS_ACCOUNT_EXPENSES,
    () => getAllEntries(getIntegrationsQuickbooksAccountExpenses),
    { ...queryConfig, refetchOnMount: true }
  );

export const useQueryIntegrationsQuickbooksAccountLiabilities = (
  queryConfig?: WSQueryConfig<any, WSServiceError>
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS_ACCOUNT_LIABILITIES,
    () => getAllEntries(getIntegrationsQuickbooksAccountLiabilities),
    { ...queryConfig, refetchOnMount: true }
  );

export const useQueryIntegrationsQuickbooksCustomers = (
  queryConfig?: WSQueryConfig<
    Await<ReturnType<typeof getIntegrationsQuickbooksCustomers>>,
    WSServiceError
  >
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS_CUSTOMERS,
    () => getAllEntries(getIntegrationsQuickbooksCustomers),
    { ...queryConfig, refetchOnMount: true }
  );

export const useQueryIntegrationsQuickbooksItems = (
  queryConfig?: WSQueryConfig<
    Await<ReturnType<typeof getIntegrationsQuickbooksItems>>,
    WSServiceError
  >
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS_ITEMS,
    () => getAllEntries(getIntegrationsQuickbooksItems),
    { ...queryConfig, refetchOnMount: true }
  );

export const useQueryIntegrationsQuickbooksVendors = (
  queryConfig?: WSQueryConfig<
    Await<ReturnType<typeof getIntegrationsQuickbooksVendors>>,
    WSServiceError
  >
) =>
  useWSQuery(
    QUERY_INTEGRATIONS_QUICKBOOKS_VENDORS,
    () => getAllEntries(getIntegrationsQuickbooksVendors),
    queryConfig
  );
